import { useCallback, useContext, useRef } from 'react'
import type { ReactNode } from 'react'
import clsx from 'clsx'
import Visibility from '../Visibility'
import DataContext from '../../DataContext/Context'
import type { SharedStateId } from '../../../../shared/helpers/useSharedState'
import {
  useSharedState,
  createReferenceKey,
} from '../../../../shared/helpers/useSharedState'
import useMounted from '../../../../shared/helpers/useMounted'
import type { InfoOverlayContent } from './setContent'
import setContent from './setContent'
import {
  Button,
  Flex,
  HeightAnimation,
  Section,
} from '../../../../components'
import type { HeightAnimationAllProps } from '../../../../components/HeightAnimation'
import { P } from '../../../../elements'
import { useTranslation } from '../../hooks'
import MainHeading from '../MainHeading'
import SubmitButton from '../SubmitButton'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

export type FormInfoOverlayProps = {
  /**
   * The content to show.
   * If not given, the children will be shown.
   * Can be `success`, `error` or a custom content.
   */
  content?: InfoOverlayContent
  onCancel?: () => void

  /** Predefined content */
  success?: {
    title?: ReactNode
    description?: ReactNode
    buttonText?: ReactNode
    buttonHref?: string
    buttonClickHandler?: () => void
  }
  /** Predefined content */
  error?: {
    title?: ReactNode
    description?: ReactNode
    retryButton?: ReactNode
    cancelButton?: ReactNode
  }

  // Various props
  id?: SharedStateId
  children: ReactNode
  className?: string
}

function InfoOverlay(props: FormInfoOverlayProps) {
  const { id: idProp, formState } = useContext(DataContext)

  const {
    id = idProp,
    content: contentProp,
    success,
    error,
    onCancel,
    className,
    children,
    ...restProps
  } = props

  const { data } = useSharedState<{
    content?: InfoOverlayContent
  }>(id ? createReferenceKey(id, 'info-overlay') : undefined)
  const { content = contentProp } = data || {}

  const translations = useTranslation()
  const mountedRef = useMounted()
  const overlayRef = useRef<HTMLDivElement>(null)
  const onAnimationEnd: HeightAnimationAllProps['onAnimationEnd'] =
    useCallback(
      (state) => {
        if (mountedRef.current && state === 'opened') {
          overlayRef.current.focus?.()
        }
      },
      [mountedRef]
    )

  // To keep the content visible while hiding it with the HightAnimation
  const currentContentRef = useRef<InfoOverlayContent>(undefined)
  if (content) {
    currentContentRef.current = content
  }

  const onCancelHandler = useCallback(() => {
    if (id) {
      setContent(id, undefined)
    }
    onCancel?.()
  }, [id, onCancel])

  const childrenAreVisible =
    typeof content !== 'undefined' ? !(content === content) : undefined
  const statusContentIsVisible =
    typeof content !== 'undefined' ? content === content : false
  const status =
    typeof content === 'string' && !content.includes(' ')
      ? content
      : undefined

  let statusContent = content

  if (currentContentRef.current === 'success') {
    const tr = translations.InfoOverlaySuccess
    const {
      title,
      description,
      buttonText,
      buttonHref,
      buttonClickHandler,
    } = success || {}

    statusContent = (
      <Section
        variant="information"
        innerSpace={{ top: 'large', bottom: 'xx-large' }}
        {...restProps}
      >
        <Flex.Stack gap="large">
          <MainHeading>{title ?? tr.title}</MainHeading>
          <P>{description ?? tr.description}</P>
          <Button
            href={buttonClickHandler ? undefined : (buttonHref ?? '/')}
            onClick={buttonClickHandler}
          >
            {buttonText ?? tr.buttonText}
          </Button>
        </Flex.Stack>
      </Section>
    )
  } else if (currentContentRef.current === 'error') {
    const tr = translations.InfoOverlayError
    const { title, description, cancelButton, retryButton } = error || {}

    statusContent = (
      <Section
        variant="transparent"
        innerSpace={{ top: 'large', bottom: 'xx-large' }}
        {...restProps}
      >
        <Flex.Stack gap="large">
          <MainHeading>{title ?? tr.title}</MainHeading>
          <HeightAnimation>
            <P>
              {formState === 'pending'
                ? tr.retryingText
                : (description ?? tr.description)}
            </P>
          </HeightAnimation>
          <Flex.Horizontal>
            <Button variant="secondary" onClick={onCancelHandler}>
              {cancelButton ?? tr.cancelButton}
            </Button>
            <SubmitButton>{retryButton ?? tr.retryButton}</SubmitButton>
          </Flex.Horizontal>
        </Flex.Stack>
      </Section>
    )
  }

  return (
    <div
      className={clsx(
        'dnb-forms-info-overlay',
        status && `dnb-forms-info-overlay--${status}`,
        'dnb-no-focus',
        className
      )}
      tabIndex={-1}
      ref={overlayRef}
    >
      <Visibility
        visible={statusContentIsVisible}
        onAnimationEnd={onAnimationEnd}
        animate
      >
        {statusContent}
      </Visibility>

      <HeightAnimation
        open={childrenAreVisible}
        onAnimationEnd={onAnimationEnd}
        animate
        keepInDOM
      >
        {children}
      </HeightAnimation>
    </div>
  )
}

InfoOverlay.setContent = setContent
withComponentMarkers(InfoOverlay, {
  _supportsSpacingProps: true,
})

export default InfoOverlay
